import { type FormEvent, type ReactNode, useRef } from 'react';

import { Banner, Flex, Text } from '@spotify-internal/encore-web';
import {
  AuthScreen,
  type FormFieldsHandle,
  FormFieldsProvider,
} from '@spotify/confidence-core-react';

import type { SurveyAnswers } from '../../config/onboardingSurvey';
import { OnboardingStepLayout } from '../OnboardingStepLayout';
import { CustomRoleFormField } from './CustomRoleFormField';
import { RoleFormField } from './RoleFormField';
import { SurveyActions } from './SurveyActions';
import { type SurveyFormValues, toSurveyAnswers } from './surveyForm';

import styles from './OnboardingSurvey.module.css';

type OnboardingSurveyProps = {
  workspaceName?: string;
  isSaving: boolean;
  hasSaveError: boolean;
  onSave: (answers: SurveyAnswers) => void;
};

const SurveyDescription = ({ workspaceName }: { workspaceName?: string }) => (
  <>
    Confidence would like to know a bit more about you, so it can tailor{' '}
    {workspaceName ? (
      <Text as="span" variant="bodyMediumBold" semanticColor="textBase">
        {workspaceName}
      </Text>
    ) : (
      'your workspace'
    )}{' '}
    to how you work.
  </>
);

const SurveyForm = ({
  isSaving,
  hasSaveError,
  onSave,
}: Omit<OnboardingSurveyProps, 'workspaceName'>) => {
  const formFieldsRef = useRef<FormFieldsHandle<SurveyFormValues>>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = formFieldsRef.current;
    if (!form) {
      return;
    }
    onSave(toSurveyAnswers(form.values));
  };

  return (
    <form onSubmit={handleSubmit} data-track-source-anchor="c41bf013de">
      <FormFieldsProvider<SurveyFormValues> ref={formFieldsRef}>
        <Flex direction="column" gap="looser">
          {hasSaveError && (
            <Banner colorSet="negative" contextual role="alert">
              Something went wrong. Try again.
            </Banner>
          )}
          <Flex direction="column" gap="tighter">
            <RoleFormField />
            <CustomRoleFormField />
          </Flex>
          <SurveyActions isSaving={isSaving} onSkip={() => onSave({})} />
        </Flex>
      </FormFieldsProvider>
    </form>
  );
};

export const OnboardingSurvey = ({
  workspaceName,
  ...formProps
}: OnboardingSurveyProps) => (
  <AuthScreen.Root>
    <AuthScreen.Main>
      <Flex direction="column" gap="looser" className={styles.layout}>
        <Flex direction="column" gap="looser" className={styles.welcome}>
          <AuthScreen.Logo />
          <AuthScreen.Title>Before you jump in</AuthScreen.Title>
          <AuthScreen.Description>
            <SurveyDescription workspaceName={workspaceName} />
          </AuthScreen.Description>
        </Flex>
        <AuthScreen.Card>
          <SurveyForm {...formProps} />
        </AuthScreen.Card>
      </Flex>
    </AuthScreen.Main>
  </AuthScreen.Root>
);

type OnboardingSurveyStepProps = OnboardingSurveyProps & {
  progress?: ReactNode;
};

export const OnboardingSurveyStep = ({
  workspaceName,
  progress,
  ...formProps
}: OnboardingSurveyStepProps) => (
  <OnboardingStepLayout
    title="Before you jump in"
    description={<SurveyDescription workspaceName={workspaceName} />}
    progress={progress}
    trackId="region.onboarding-survey"
  >
    <SurveyForm {...formProps} />
  </OnboardingStepLayout>
);
